import { api } from '@hunk/shared/api';
import { type AuthInfoRes, EMAIL_CODE_LENGTH, EMAIL_RE, TELEGRAM_LOGIN_URL } from '@hunk/shared/api/apiAuth';
import { ArrowRight, Loader2, Mail, Send } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';

import { Captcha } from '@/components/Captcha';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSession, waitText } from '@/hooks/useSession';

/** Mini App (/mini): no login form - initData only. Shown when it failed or Telegram did not supply it. */
function MiniAppLogin() {
  const { error, missingInitData, relogin } = useSession();
  return (
    <Shell>
      <h1 className="text-xl font-bold">כניסה</h1>
      <p className="text-sm text-neutral-400">{missingInitData ? 'יש לפתוח את החנות מתוך הבוט בטלגרם' : 'ההתחברות עם טלגרם נכשלה'}</p>
      {error && <p className="text-destructive text-sm">{error}</p>}
      {missingInitData ? (
        <Button asChild variant="outline">
          <a href="/">מעבר לאתר</a>
        </Button>
      ) : (
        <Button variant="outline" onClick={() => void relogin()}>
          נסו שוב להתחבר עם טלגרם
        </Button>
      )}
    </Shell>
  );
}

export function LoginPage() {
  const { isMiniApp } = useSession();
  return isMiniApp ? <MiniAppLogin /> : <WebLogin />;
}

/** a local path to return to after login (?next=/checkout) - never another site */
const safeNext = (v: string | null) => (v && v.startsWith('/') && !v.startsWith('//') ? v : '/');

/**
 * Website: "Log in with Telegram" (when configured) or an email one-time code (2 steps). The account is created on the
 * first login either way; ?next= sends the user back (checkout, orders).
 */
function WebLogin() {
  const { status, error, requestEmailCode, verifyEmailCode } = useSession();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [info, setInfo] = useState<AuthInfoRes>();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const [localError, setLocalError] = useState<string>();
  /** solved captcha payload ('' = not solved); one solution per code request */
  const [captcha, setCaptcha] = useState('');

  useEffect(() => {
    api.auth
      .authInfo()
      .then((r) => setInfo(r.response))
      .catch(() => setInfo({ telegramWeb: false, email: true }));
  }, []);
  // the Telegram login comes back to "/" - remember where to go after it
  useEffect(() => {
    try {
      sessionStorage.setItem('hunk.next', next);
    } catch {
      // private mode
    }
  }, [next]);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  if (status === 'authenticated') return <Navigate to={next} replace />;

  const sendCode = async (e?: FormEvent) => {
    e?.preventDefault();
    setLocalError(undefined);
    const addr = email.trim().toLowerCase();
    if (!EMAIL_RE.test(addr)) return setLocalError('כתובת האימייל אינה תקינה');
    if (!captcha) return setLocalError('סמנו "אני לא רובוט" ונסו שוב');
    setBusy(true);
    const r = await requestEmailCode(addr, captcha);
    setBusy(false);
    setCaptcha(''); // used up - a new code needs a new captcha
    if (r !== false) {
      setStep('code');
      setCode('');
      setWait(r);
    }
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(undefined);
    if (code.length !== EMAIL_CODE_LENGTH) return setLocalError(`הקלידו את ${EMAIL_CODE_LENGTH} הספרות מהאימייל`);
    setBusy(true);
    await verifyEmailCode(email.trim().toLowerCase(), code);
    setBusy(false);
  };
  const shownError = localError ?? error;

  return (
    <Shell>
      <h1 className="text-xl font-bold">כניסה / הרשמה</h1>
      <p className="text-sm text-neutral-400">התחברו כדי להשלים את ההזמנה ולעקוב אחריה</p>
      {shownError && <p className="text-destructive text-sm">{shownError}</p>}
      {!info ? (
        <Loader2 className="mx-auto size-6 animate-spin text-neutral-500" />
      ) : (
        <>
          {info.telegramWeb && (
            <Button asChild size="lg" className="rounded-full bg-[#2aabee] bg-none text-white hover:bg-[#229ed9]">
              <a href={TELEGRAM_LOGIN_URL}>
                <Send />
                התחברות עם טלגרם
              </a>
            </Button>
          )}
          {info.telegramWeb && info.email && (
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <span className="h-px flex-1 bg-white/10" />
              או עם אימייל
              <span className="h-px flex-1 bg-white/10" />
            </div>
          )}
          {info.email &&
            (step === 'email' ? (
              <form className="flex flex-col gap-3" onSubmit={(e) => void sendCode(e)}>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">אימייל</Label>
                  <Input id="email" type="email" autoComplete="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <Captcha onChange={setCaptcha} />
                <Button type="submit" size="lg" disabled={busy} className="rounded-full font-bold">
                  {busy ? <Loader2 className="animate-spin" /> : <Mail />}
                  שליחת קוד כניסה
                </Button>
              </form>
            ) : (
              <form className="flex flex-col gap-3" onSubmit={(e) => void verify(e)}>
                <p className="text-sm">
                  שלחנו קוד בן {EMAIL_CODE_LENGTH} ספרות אל{' '}
                  <b dir="ltr" className="inline-block">
                    {email.trim()}
                  </b>
                </p>
                <Input
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  dir="ltr"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, EMAIL_CODE_LENGTH))}
                  placeholder={'•'.repeat(EMAIL_CODE_LENGTH)}
                  className="h-12 text-center text-2xl tracking-[0.5em]"
                />
                <Button type="submit" size="lg" disabled={busy} className="rounded-full font-bold">
                  {busy && <Loader2 className="animate-spin" />}
                  כניסה
                </Button>
                <div className="flex justify-between text-sm">
                  <button type="button" className="text-neutral-400 hover:text-white" onClick={() => setStep('email')}>
                    שינוי אימייל
                  </button>
                  <button type="button" disabled={wait > 0 || busy} className="text-primary font-semibold disabled:text-neutral-600" onClick={() => setStep('email')}>
                    {wait > 0 ? `קוד חדש בעוד ${waitText(wait)}` : 'שליחת קוד חדש'}
                  </button>
                </div>
              </form>
            ))}
          {!info.telegramWeb && !info.email && <p className="text-sm text-amber-400">ההתחברות אינה מוגדרת בשרת.</p>}
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-svh flex-col items-center justify-center bg-[radial-gradient(ellipse_at_top,#4a0a0d_0%,#000_60%)] p-4">
      <Link to="/" aria-label="חזרה לחנות" className="mb-6">
        <Logo size="lg" />
      </Link>
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-2xl border border-white/10 bg-neutral-950 p-6 shadow-2xl">{children}</div>
      <Link to="/" className="mt-5 flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white">
        <ArrowRight className="size-4" />
        חזרה לחנות
      </Link>
    </main>
  );
}
